import React, { useCallback, FormEvent, RefObject } from 'react';
import styled, { css } from 'styled-components';
import { body1, plainTextStyle } from '@westwing/ui-kit/typography';
import { forTablet, forIE, forDesktop } from '@westwing/ui-kit/breakPoints';
import Search from '../Icons/Svgs/v2/Search';
import { CrossIcon } from '../Icons/StyledIcons';
import {
    mobileActiveSearch,
    desktopActiveSearchInput,
} from '../../../../oneHeader/local/Search/ResponsiveSearchStyles';
import { VictorQaClasses, BellaTestIds } from '../../../../common/qaIds';

const Input = styled.input`
    ${plainTextStyle}
    width: 100%;
    border: none;
    font-size: 16px; /* special fix to prevent zoom problem */
    background: transparent;
    ${forTablet`
        color: ${({ theme }) => theme.colors.charcoal100};
    `}
`;

const SEARCH_RESET_VALUE = '';

const SearchInputLayout = styled.div<{ isSearchActive: boolean }>`
    display: grid;
    grid-template: 'input clear-icon search-icon' 32px / 1fr auto 30px;
    justify-items: center;
    align-items: center;
    width: 100%;
    position: relative;

    ${({ isSearchActive }) => (isSearchActive ? mobileActiveSearch : '')}

    ${forDesktop`
        max-width: 285px;
        background: none;
        border-bottom: 1px solid rgb(50, 50, 50);
        padding: 0;
        min-height: 32px;
        margin: 0 auto;
        grid-template: 'input clear-icon search-icon' 32px / 1fr auto 30px;
    `}

    ${forIE`
        display: flex;
    `}
`;

const SearchIcon = styled(Search)`
    grid-area: search-icon;
    color: ${({ theme }) => theme.colors.charcoal100};

    ${forDesktop`
         display: block;
    `}
    ${forTablet`
        opacity: 1;
    `}
    ${forIE`
        -ms-grid-column: 2;
    `}
`;

const StyledInput = styled(Input)<{ isSearchActive: boolean }>`
    ${body1}
    ${({ isSearchActive }) =>
        isSearchActive
            ? css`
                  appearance: none;
                  background: ${({ theme }) => theme.colors.colorBackgroundGrey};
                  ::placeholder {
                      line-height: normal;
                  }
              `
            : ''}

    ${forTablet`
        color: ${({ theme }) => theme.colors.charcoal100};
        ::placeholder {
           
            color: ${({ theme }) => theme.colors.charcoal100};
        }
        display: block;
    `}
    ${({ isSearchActive }) => (isSearchActive ? desktopActiveSearchInput : '')}
`;

const ClearInputIcon = styled.div<{ isSearchActive: boolean; hasSearchValue: boolean }>`
    grid-column: clear-icon;
    display: none;
    padding: 8px;

    ${({ isSearchActive, hasSearchValue }) =>
        isSearchActive && hasSearchValue
            ? css`
                  display: block;
              `
            : ''}
    ${({ isSearchActive }) =>
        isSearchActive
            ? forDesktop`
                  display: none;
              `
            : ''}
`;

const proactivelyFetchSuggestionsModule = () => {
    // eslint-disable-next-line no-unused-expressions
    import('../../../../oneHeader/local/Search/Suggestions');
};

const StyledForm = styled.form<{ isSearchActive: boolean }>`
    grid-area: input;
    width: 100%;
    display: ${({ isSearchActive }) => (isSearchActive ? 'block' : 'none')};

    ${forDesktop`
        display: block;
    `}

    ${forIE`
        -ms-grid-column: 1;
    `}
`;

export interface SearchInputSharedProps {
    placeholder: string;
    value: string;
    onChange: (searchString: string) => void;
    onKeyUp: (keyCode: number) => void;
}
interface SearchInputProps extends SearchInputSharedProps {
    handleTargetClick: () => void;
    clearSuggestions: () => void;
    isSearchActive: boolean;
    inputRef: RefObject<HTMLInputElement>;
    handleFocus?: () => void;
}

const SearchInput: React.SFC<SearchInputProps> = ({
    placeholder,
    onChange,
    value,
    onKeyUp,
    handleTargetClick,
    clearSuggestions,
    isSearchActive,
    inputRef,
    handleFocus,
}) => {
    const onInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            onChange(event.target.value);
            event.preventDefault();
        },
        [onChange]
    );

    const onKeyUpEvent = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            onKeyUp(event.keyCode);
            event.preventDefault();
        },
        [onKeyUp]
    );

    const onFocusEvent = useCallback(() => {
        proactivelyFetchSuggestionsModule();
        handleTargetClick();
        if (handleFocus) {
            handleFocus();
        }
    }, [handleFocus, handleTargetClick]);

    const onClickClearInput = useCallback(() => {
        onChange(SEARCH_RESET_VALUE);
        clearSuggestions();
    }, [clearSuggestions, onChange]);

    // We need a form element to make ios display its native keyboard search button.
    const onSubmitHandler = (event: FormEvent<HTMLFormElement>) => event.preventDefault();

    return (
        <SearchInputLayout role="search" isSearchActive={isSearchActive}>
            <StyledForm action="" onSubmit={onSubmitHandler} isSearchActive={isSearchActive}>
                <StyledInput
                    type="search"
                    placeholder={placeholder}
                    value={value}
                    onChange={onInputChange}
                    onKeyUp={onKeyUpEvent}
                    onFocus={onFocusEvent}
                    ref={inputRef}
                    isSearchActive={isSearchActive}
                    data-testid="search-input"
                    className={VictorQaClasses.headerSearchInput}
                />
            </StyledForm>
            <ClearInputIcon
                role="button"
                onClick={onClickClearInput}
                isSearchActive={isSearchActive}
                hasSearchValue={!!value}
                className={VictorQaClasses.headerSearchInputClearIcon}
                data-testid={BellaTestIds.searchInputClearIcon}
            >
                <CrossIcon />
            </ClearInputIcon>
            <SearchIcon
                onClick={onFocusEvent}
                data-testid="header-search-icon"
                className={VictorQaClasses.headerSearchIcon}
            />
        </SearchInputLayout>
    );
};

export default SearchInput;
